import { useCallback, useReducer } from "react";
import { GridData } from "../../components/ag-grid-extensions/models/GridData";
import { GridApi } from "ag-grid-community";
import { ActionMenuItem } from "../../components/action-menu/ActionMenuItem";
import { actions } from "./RolePermissionsReportActions";
import { sortAndDispatchActions } from "../../common/utilities/SortAndDispatchActions";

export type RolePermissionsReportStateType = {

    organizationID: number | undefined;
    data: GridData | undefined;
    gridApi: GridApi | undefined;
    downloadUid: string | undefined;
    actions: ActionMenuItem[]
};

const defaultRolePermissionsReportState: RolePermissionsReportStateType = {
    
    organizationID: undefined,
    data: undefined,
    gridApi: undefined,
    downloadUid: undefined,
    actions: actions
};

export {defaultRolePermissionsReportState}

enum RolePermissionsReportStateActionType {
    OrganizationChanged = 'ROLE_PERMISSIONS_ORGANIZATION_CHANGED',
    DataChanged = 'ROLE_PERMISSIONS_REPORT_DATA_CHANGED',
    DownloadUidChanged = 'ROLE_PERMISSIONS_REPORT_DOWNLOAD_UID_CHANGED',
    GridApiChanged = 'ROLE_PERMISSIONS_REPORT_GRID_API_CHANGED',
    ActionsChanged = 'ROLE_PERMISSIONS_REPORT_ACTIONS_CHANGED'
}

type RolePermissionsReportStateActions = | {
    type: RolePermissionsReportStateActionType.DataChanged;
    payload: GridData;
} | {
    type: RolePermissionsReportStateActionType.GridApiChanged;
    payload: GridApi;
} | {
    type: RolePermissionsReportStateActionType.OrganizationChanged;
    payload: number | undefined;
} | {
    type: RolePermissionsReportStateActionType.DownloadUidChanged;
    payload: string | undefined;
} | {
    type: RolePermissionsReportStateActionType.ActionsChanged;
    payload: ActionMenuItem[];
};

function rolePermissionsReportStateReducer(state: RolePermissionsReportStateType, 
    action: RolePermissionsReportStateActions): RolePermissionsReportStateType {

    const copy = {...state};
    switch (action.type) {
        
        case RolePermissionsReportStateActionType.OrganizationChanged:
            copy.organizationID = action.payload;
            return copy;

        case RolePermissionsReportStateActionType.DataChanged:
            copy.data = action.payload;
            return copy;

        case RolePermissionsReportStateActionType.GridApiChanged:
            copy.gridApi = action.payload;
            return copy;

    
        case RolePermissionsReportStateActionType.DownloadUidChanged:
            copy.downloadUid = action.payload;
            return copy;

        case RolePermissionsReportStateActionType.ActionsChanged:
            copy.actions = action.payload;
            return copy;
    
        default:
            return state;
    }
}

export default function useRolePermissionsReportState() {

    const [state, dispatch] = useReducer(rolePermissionsReportStateReducer, {...defaultRolePermissionsReportState});

    const setData = useCallback((value: GridData) => dispatch({
        type: RolePermissionsReportStateActionType.DataChanged,
        payload: value
    }), []);

    const setGridApi = useCallback((value: GridApi) => dispatch({
        type: RolePermissionsReportStateActionType.GridApiChanged,
        payload: value
    }), []);

    const setDownloadUid = useCallback((value: string | undefined) => dispatch({
        type: RolePermissionsReportStateActionType.DownloadUidChanged,
        payload: value
    }), []);

    const setOrganization = useCallback((value: number | undefined) => dispatch({
        type: RolePermissionsReportStateActionType.OrganizationChanged,
        payload: value
    }), []);

    // const setActions = useCallback((value: ActionMenuItem[]) => dispatch({
    //     type: RolePermissionsReportStateActionType.ActionsChanged,
    //     payload: value
    // }), []);

    const setActions = useCallback((actions: ActionMenuItem[]) => sortAndDispatchActions(
        state.actions,
        actions,
        (sortedActions) => dispatch({
            type: RolePermissionsReportStateActionType.ActionsChanged,
            payload: sortedActions
        })
    ), [state.actions]);

    return {
        state,
        setData,
        setGridApi,
        setDownloadUid,
        setOrganization,
        setActions
    };
}

